
import img0 from './Images/Encryption_Settings2.png';
import img1 from './Images/Export_Configuration_File.png';
  
const content = `# Automate OnData Encryption Tool

The OnData Encryption Tool can be automated as part of your DevOps processes, especially when you plan to use the OnData Encryption Tool to regularly refresh your 
lower environments (for example, Development, Testing, Staging, and Training) with encrypted/obfuscated data from your Production databases. Your automation scripts 
may perform the following steps automatically:
* Restore a copy of your Production database to a database server in the Production environment
* Launch the OnData Encryption Tool to process the restored database
* Capture the processed database as a database backup
* Copy the database backup to your lower environments
* Restore the database backup to a database server in your lower environments

Below are the steps on how to automatically process a database using the OnData Encryption Tool.  

## 1. Generating Configuration File for OnData Encryption Tool
Instead of manually interacting with the OnData Encryption Tool to process a database, you can pass a configuration file as a command line parameter to the OnData 
Encryption Tool so that it can start processing without human interaction. The best way to generate such a configuration file is to use the GUI version of the 
OnData Encryption Tool on Windows. You can follow the steps in \`Section 1. Run GUI Version of OnData Encryption Tool on Windows\` of the instructions in 
[**Use OnData Encryption Tool**](reference_doc#securedb/ondata_encryption/use_ondata_encryption_tool). After you finish the configuration in \`Step 1.7 Encryption Settings\`, you can click on the 
\`Export configuration file\` button to export a copy of the configuration file as a JSON file.  
  
![Encryption Settings](${ img0 })  
  
After the configuration file is exported, you can click on the \`View Configuration File\` link to locate the JSON file. 
  
![Export Configuration File](${ img1 })  
  
You can edit the exported configuration file as needed. A sample configuration file exported from the OnData Encryption Tool is illustrated below:  

\`\`\`JSON
{
  "OnDataDatabaseName": "Northwind",
  "DatabaseType": 3,
  "ServerAddress": "AUSW11A3\\\\SQLEXPRESS",
  "Port": 1433,
  "TNSAdmin": "",
  "TNSServiceName": "",
  "IntegratedSecurity": true,
  "IsUserInDatabase": false,
  "Username": "",
  "Password": null,
  "DatabaseName": "Northwind",
  "EndpointUrl": "",
  "AuthorizationKey": null,
  "Account": "",
  "Warehouse": "",
  "OperatingMode": 0,
  "OperatingEnvironment": 0,
  "ApplyObfuscationRules": true,
  "SubsettingEnabled": false
}
\`\`\`

## 2. Running OnData Encryption Tool with Configuration File
With the configuration file, you can run the OnData Encryption Tool from a Windows command prompt or Linux shell to automate the database processing without 
human interaction. The same configuration file can be used for both Windows and Linux versions of the OnData Encryption Tool. The OnData Encryption Tool supports 
a number of command line options, some of which can be used to override the configuration entries inside the configuration file. Below is the list of options:  

\`\`\`bash
  -p, --password            Set password of the database user.

  -t, --targetPassword      Set password of the target database user.

  -m, --mode                Set the operating mode.
                            Encryption
                            Decryption
                            MigrateAndEncrypt

  -c, --configPath          Set the config file path.

  -r, --resumeOption        Set the resume option.
                             - The blank option is the default option, will ask the user during processing.
                            y - Always resume if last run is failed.
                            n - Never resume if last run is failed.

  -s, --skipInvalidInput    Whether to skip invalid input.

  -l, --logErrorData        Whether to log the original data of columns that failed on encryption.

  --help                    Display this help screen.

  --version                 Display version information.
\`\`\`

You can use the \`-c\` or \`--configPath\` option to pass the configuration file. For example, you can use the following command line in your automation scripts:

**Windows**:
\`\`\`bash

OnDataEncryptionTool.exe -c "/full/path/to/configuration/file"

\`\`\`
**Linux**:
\`\`\`bash

OnDataEncryptionTool -c "/full/path/to/configuration/file"

\`\`\`

**Note**: To automate the processing of DB2 databases on Linux, a Linux shell script \`db2-start-encryption-tool.sh\` is provided in the OnData Encryption Tool 
package. Please use that script for automation, instead of running the OnData Encryption Tool directly.  

\`\`\`bash

./db2-start-encryption-tool.sh -c "/full/path/to/configuration/file"

\`\`\``
  
export default content;
